import { ButtonLink } from '@/components/ui/button';
import { Boxes, FileCode2, KeyRound, ListChecks, ShieldAlert } from 'lucide-react';
import { Page, PageHeader } from '@/components/layout/page';
import { CodeBlock, DevNote } from '@/components/ui/dev-note';
import { Alert, Badge, Card, CardBody, CardHeader, Container, Section } from '@/components/ui/primitives';
import { appConfig, maskKey } from '@/config/env';
import { useSite } from '@/hooks/use-site';
import { paths } from '@/lib/paths';

/**
 * Page « Notes d'intégration » — le mode d'emploi de ce site d'exemple.
 *
 * Elle n'existe évidemment pas dans un vrai site : c'est la page que liront
 * les développeurs qui reprennent ce projet comme point de départ. Elle
 * récapitule ce que couvre l'exemple, où chaque chose se trouve dans le code,
 * et les quelques règles à ne pas contourner.
 */
export default function IntegrationPage() {
    return (
        <Page title="Notes d'intégration">
            <PageHeader
                eyebrow="Pour les développeurs"
                title="Notes d'intégration"
                description="Ce site est une intégration de référence de l'API Dash Lite. Cette page décrit ce qu'il couvre, où le trouver dans le code, et les règles qui comptent."
            />

            <Section>
                <Container className="max-w-4xl space-y-12">
                    <CurrentConfiguration />
                    <EndpointCoverage />
                    <CodeMap />
                    <GoldenRules />
                    <RobotAttributes />
                    <TestingErrors />
                    <GoingLive />
                </Container>
            </Section>
        </Page>
    );
}

/** Ce que le front a réellement en configuration, à l'instant présent. */
function CurrentConfiguration() {
    const site = useSite();

    return (
        <Card>
            <CardHeader
                title="Configuration courante de ce front"
                description="Trois variables d'environnement, et rien d'autre."
                icon={<KeyRound className="size-4" />}
                action={
                    <Badge tone={site.environment === 'live' ? 'positive' : 'brand'}>
                        {site.environment === 'live' ? 'Production' : 'Test'}
                    </Badge>
                }
            />

            <CardBody className="space-y-4">
                <CodeBlock label=".env">
                    {`VITE_DASHLITE_SITE_KEY=${maskKey(appConfig.siteKey)}
VITE_DASHLITE_API_URL=${appConfig.apiUrl}
VITE_SHOW_API_INSPECTOR=${appConfig.showApiInspector}`}
                </CodeBlock>

                <p className="text-ink-soft text-sm leading-relaxed">
                    L'environnement affiché ci-dessus ne vient pas du préfixe de la clé : c'est le champ{' '}
                    <code className="font-mono text-xs">environment</code> renvoyé par{' '}
                    <code className="font-mono text-xs">GET /site</code>. Passer en production consiste à remplacer{' '}
                    <code className="font-mono text-xs">site_test_…</code> par{' '}
                    <code className="font-mono text-xs">site_live_…</code> — la base d'URL de l'API ne change pas, et
                    aucune ligne de code n'est à toucher.
                </p>

                <Alert tone="info" title="Cette clé n'est pas un secret">
                    Comme une clé publishable Stripe, elle vit dans le bundle JavaScript envoyé au navigateur. Elle ne
                    donne accès qu'aux données de ce site, dans son environnement. Les secrets — clés Stripe secrètes,
                    accès aux fournisseurs de données — restent côté plateforme.
                </Alert>
            </CardBody>
        </Card>
    );
}

const ENDPOINTS: Array<{ method: string; path: string; page: string; to: string; auth: boolean }> = [
    { method: 'GET', path: '/site', page: 'Chargé au démarrage', to: paths.home, auth: false },
    { method: 'GET', path: '/companies/search', page: 'Recherche, aperçu du paiement', to: paths.search, auth: false },
    { method: 'GET', path: '/companies/{id}', page: 'Fiche entreprise', to: paths.search, auth: true },
    { method: 'GET', path: '/offer', page: 'Tarif (via /site)', to: paths.pricing, auth: false },
    { method: 'POST', path: '/payment', page: 'Paiement', to: paths.checkout, auth: false },
    { method: 'POST', path: '/payment/claim', page: 'Paiement', to: paths.checkout, auth: false },
    { method: 'GET', path: '/entitlement', page: 'Confirmation, compte', to: paths.account, auth: true },
    { method: 'POST', path: '/auth/login', page: 'Connexion', to: paths.login, auth: false },
    { method: 'POST', path: '/auth/logout', page: 'Mon compte', to: paths.account, auth: true },
    { method: 'POST', path: '/auth/password', page: 'Mon compte', to: paths.account, auth: true },
    { method: 'POST', path: '/auth/forgot-password', page: 'Mot de passe oublié', to: paths.forgotPassword, auth: false },
    { method: 'POST', path: '/auth/reset-password', page: 'Nouveau mot de passe', to: paths.resetPassword, auth: false },
    { method: 'GET', path: '/me', page: 'Mon compte', to: paths.account, auth: true },
    { method: 'DELETE', path: '/me', page: 'Mon compte', to: paths.account, auth: true },
    { method: 'POST', path: '/billing/portal', page: 'Mon compte', to: paths.account, auth: true },
    { method: 'POST', path: '/subscription/lookup', page: 'Résiliation', to: paths.cancelSubscription, auth: false },
    { method: 'POST', path: '/subscription/cancel', page: 'Résiliation', to: paths.cancelSubscription, auth: false },
    { method: 'POST', path: '/newsletter/subscribe', page: 'Newsletter, pied de page', to: paths.newsletter, auth: false },
    { method: 'POST', path: '/newsletter/unsubscribe', page: 'Désinscription', to: paths.unsubscribe, auth: false },
    { method: 'POST', path: '/support/contact', page: 'Contact', to: paths.contact, auth: false },
];

function EndpointCoverage() {
    return (
        <section>
            <h2 className="font-display flex items-center gap-2 text-2xl tracking-tight">
                <ListChecks className="text-brand-600 size-5" aria-hidden />
                Les endpoints couverts
            </h2>

            <p className="text-ink-soft mt-3 text-sm leading-relaxed">
                Ce site appelle l'intégralité de l'API publique. Chaque ligne renvoie vers l'écran qui en fait la
                démonstration — ouvrez l'inspecteur d'API en bas à droite pour observer les échanges en direct.
            </p>

            <div className="border-line mt-6 overflow-x-auto rounded-xl border">
                <table className="w-full min-w-[36rem] text-sm">
                    <thead className="bg-surface-sunken text-ink-muted text-xs uppercase">
                        <tr>
                            <th scope="col" className="px-4 py-2.5 text-left font-semibold">
                                Endpoint
                            </th>
                            <th scope="col" className="px-4 py-2.5 text-left font-semibold">
                                Session requise
                            </th>
                            <th scope="col" className="px-4 py-2.5 text-left font-semibold">
                                Écran
                            </th>
                        </tr>
                    </thead>

                    <tbody className="divide-line divide-y">
                        {ENDPOINTS.map((endpoint) => (
                            <tr key={`${endpoint.method} ${endpoint.path}`}>
                                <td className="px-4 py-2.5 font-mono text-xs">
                                    <span className="text-brand-600 font-semibold">{endpoint.method}</span>{' '}
                                    {endpoint.path}
                                </td>
                                <td className="px-4 py-2.5">
                                    {endpoint.auth ? (
                                        <Badge tone="brand">Bearer</Badge>
                                    ) : (
                                        <span className="text-ink-muted text-xs">Clé de site seule</span>
                                    )}
                                </td>
                                <td className="px-4 py-2.5">
                                    <ButtonLink to={endpoint.to} variant="ghost" size="sm" className="px-1">
                                        {endpoint.page}
                                    </ButtonLink>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </section>
    );
}

const CODE_MAP = [
    { path: 'src/config/env.ts', role: "Lecture et validation des variables d'environnement" },
    { path: 'src/lib/dashlite.ts', role: 'Instanciation du client SDK, persistance du token' },
    { path: 'src/lib/dashlite-envelope.ts', role: "Appel HTTP direct pour lire l'enveloppe { data, meta }" },
    { path: 'src/lib/errors.ts', role: "Catalogue des codes d'erreur → messages et réactions" },
    { path: 'src/lib/format.ts', role: 'Montants en centimes, dates ISO, description des offres' },
    { path: 'src/hooks/use-site.tsx', role: 'Contexte de configuration du site (GET /site)' },
    { path: 'src/hooks/use-session.tsx', role: 'Token, profil, droit d’accès' },
    { path: 'src/hooks/use-company-search.ts', role: 'Recherche déboncée, réponses obsolètes ignorées' },
    { path: 'src/components/payment/checkout.tsx', role: 'Tunnel de paiement complet (Stripe Elements)' },
    { path: 'src/components/company/company-sheet.tsx', role: 'Rendu du schéma normalisé multi-pays' },
    { path: 'src/components/api-inspector/', role: "L'inspecteur d'API — à supprimer dans un vrai site" },
];

function CodeMap() {
    return (
        <section>
            <h2 className="font-display flex items-center gap-2 text-2xl tracking-tight">
                <FileCode2 className="text-brand-600 size-5" aria-hidden />
                Où trouver quoi
            </h2>

            <p className="text-ink-soft mt-3 text-sm leading-relaxed">
                Chaque fichier porte en tête un commentaire expliquant son rôle et les décisions d'intégration qu'il
                incarne. Les onze fichiers ci-dessous sont ceux qui méritent une lecture attentive.
            </p>

            <ul className="border-line divide-line mt-6 divide-y rounded-xl border">
                {CODE_MAP.map((entry) => (
                    <li key={entry.path} className="flex flex-wrap items-baseline gap-x-4 gap-y-1 px-4 py-3">
                        <code className="text-brand-700 font-mono text-xs">{entry.path}</code>
                        <span className="text-ink-soft text-sm">{entry.role}</span>
                    </li>
                ))}
            </ul>
        </section>
    );
}

const RULES = [
    {
        title: 'Brancher la logique sur error.code, jamais sur error.message',
        body: "Les messages sont en anglais, destinés au développeur, et peuvent être reformulés sans préavis. Les codes sont stables et versionnés. Le catalogue complet, avec la réaction recommandée pour chacun, est traduit dans src/lib/errors.ts.",
    },
    {
        title: "has_access est le seul interrupteur d'accès",
        body: "Ne recalculez jamais l'accès à partir de status, starts_at ou ends_at : le champ tient déjà compte du délai de grâce après un échec de paiement et de la fin de période payée après une résiliation. Un calcul maison divergerait du serveur.",
    },
    {
        title: 'Le pays est implicite',
        body: "Aucun code pays n'est jamais transmis : la clé de site suffit. Le backend route vers le bon registre national et renvoie exactement le même format de réponse. Un front écrit pour la France fonctionne tel quel ailleurs.",
    },
    {
        title: 'payment_id est opaque',
        body: "Sa forme dépend du type d'offre (pi_… ou sub_…). Conservez-le et renvoyez-le tel quel au claim, sans l'interpréter ni le préfixer.",
    },
    {
        title: 'Un token refusé est un token à jeter',
        body: "Sur unauthenticated ou environment_mismatch, purgez immédiatement le token stocké. Le second code signale typiquement un token de test resté dans le navigateur après une bascule en production.",
    },
    {
        title: 'Débouncez la recherche',
        body: "GET /companies/search a un quota plus serré que le reste de l'API. Sans temporisation, une frappe rapide obtient un 429 rate_limited en quelques secondes.",
    },
];

function GoldenRules() {
    return (
        <section>
            <h2 className="font-display flex items-center gap-2 text-2xl tracking-tight">
                <Boxes className="text-brand-600 size-5" aria-hidden />
                Les six règles qui comptent
            </h2>

            <div className="mt-6 grid gap-4 sm:grid-cols-2">
                {RULES.map((rule, index) => (
                    <Card key={rule.title}>
                        <CardBody>
                            <p className="text-brand-600 font-mono text-xs">{String(index + 1).padStart(2, '0')}</p>
                            <h3 className="text-ink mt-1 font-sans text-sm font-semibold">{rule.title}</h3>
                            <p className="text-ink-soft mt-2 text-sm leading-relaxed">{rule.body}</p>
                        </CardBody>
                    </Card>
                ))}
            </div>
        </section>
    );
}

function RobotAttributes() {
    return (
        <section>
            <h2 className="font-display text-2xl tracking-tight">Les attributs data-dashlite</h2>

            <p className="text-ink-soft mt-3 text-sm leading-relaxed">
                Avant la mise en production, un robot déroule votre tunnel réel avec la carte de test : il achète, puis
                vérifie que l'accès s'est ouvert. Il n'y a pas d'inscription à traverser — un compte naît du paiement.
                Ces quatre attributs lui indiquent son chemin. Ils sont invisibles pour vos visiteurs et n'ont aucun
                effet sur votre mise en page.
            </p>

            <div className="mt-6 space-y-3">
                {[
                    { attribute: 'offer-checkout', where: "bouton d'achat de l'offre", file: 'components/payment/offer-card.tsx' },
                    { attribute: 'payment-email', where: 'champ email du formulaire de paiement', file: 'components/payment/checkout.tsx' },
                    { attribute: 'payment-submit', where: 'bouton qui valide le paiement', file: 'components/payment/checkout.tsx' },
                    { attribute: 'access-granted', where: "élément affiché une fois l'accès ouvert", file: 'pages/checkout-success.tsx' },
                ].map((entry) => (
                    <div key={entry.attribute} className="border-line flex flex-wrap items-baseline gap-x-4 gap-y-1 rounded-lg border px-4 py-3">
                        <code className="text-brand-700 font-mono text-xs">data-dashlite="{entry.attribute}"</code>
                        <span className="text-ink-soft text-sm">{entry.where}</span>
                        <code className="text-ink-muted ml-auto font-mono text-xs">{entry.file}</code>
                    </div>
                ))}
            </div>

            <p className="text-ink-muted mt-4 text-sm">
                Les champs carte eux-mêmes n'ont rien à porter : ils vivent dans les iframes de Stripe Elements, que le
                robot sait remplir.
            </p>
        </section>
    );
}

function TestingErrors() {
    return (
        <section>
            <h2 className="font-display flex items-center gap-2 text-2xl tracking-tight">
                <ShieldAlert className="text-brand-600 size-5" aria-hidden />
                Provoquer les cas d'erreur
            </h2>

            <p className="text-ink-soft mt-3 text-sm leading-relaxed">
                En environnement de test, des valeurs magiques déclenchent les cas limites à la demande, sans consommer
                de crédits fournisseur. Indispensable pour blinder vos écrans d'erreur.
            </p>

            <div className="mt-6">
                <CodeBlock label="À essayer dans la barre de recherche ou dans l'URL">
                    {`# 404 company_not_found
/entreprise/000000000

# 503 upstream_unavailable
/entreprise/999999999

# 503 upstream_unavailable sur la recherche
/recherche?q=__unavailable__

# 403 entitlement_required
/entreprise/111222333   (sans accès actif)`}
                </CodeBlock>
            </div>

            <DevNote title="Carte de test Stripe" className="mt-6">
                <p>
                    En environnement de test, la carte <span className="font-mono">4242 4242 4242 4242</span> aboutit
                    toujours, avec n'importe quelle date future et n'importe quel cryptogramme. Les autres cartes de
                    test de Stripe (refus, authentification forte obligatoire) fonctionnent également.
                </p>
            </DevNote>
        </section>
    );
}

function GoingLive() {
    return (
        <section>
            <h2 className="font-display text-2xl tracking-tight">Passer en production</h2>

            <p className="text-ink-soft mt-3 text-sm leading-relaxed">
                Côté front, une seule variable change. Le reste relève de la configuration du site.
            </p>

            <div className="mt-6">
                <CodeBlock label="Bascule">
                    {`# Développement
VITE_DASHLITE_SITE_KEY=site_test_xxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx

# Production
VITE_DASHLITE_SITE_KEY=site_live_xxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx`}
                </CodeBlock>
            </div>

            <ul className="text-ink-soft mt-6 space-y-2 text-sm">
                <li>
                    <strong className="text-ink">Données</strong> — les fixtures fictives laissent place aux registres
                    officiels.
                </li>
                <li>
                    <strong className="text-ink">Paiements</strong> — les cartes de test laissent place aux vrais
                    encaissements.
                </li>
                <li>
                    <strong className="text-ink">CORS</strong> — <code className="font-mono text-xs">localhost</code>{' '}
                    n'est plus autorisé : le domaine de production doit être déclaré, en HTTPS.
                </li>
                <li>
                    <strong className="text-ink">Sessions</strong> — un token de test resté dans le navigateur provoque
                    un <code className="font-mono text-xs">403 environment_mismatch</code> ; ce front purge alors la
                    session automatiquement.
                </li>
            </ul>

            <Alert tone="caution" title="Videz le stockage local à la bascule" className="mt-6">
                C'est le piège classique. Ce site s'en protège dans{' '}
                <code className="font-mono text-xs">src/hooks/use-session.tsx</code> : au démarrage, tout token stocké
                est vérifié auprès de l'API, et purgé s'il est refusé.
            </Alert>
        </section>
    );
}
